import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE, ACTION_CATEGORY} from 'constants/AnalyticsConstants';
import * as EventConstants from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import ProteinActions from 'molbio/proteins/ProteinActions';
import ProteinDAO from 'molbio/proteins/ProteinDAO';
import ProteinStore from 'molbio/proteins/ProteinStore';
import handleProteinUpdateError from 'molbio/proteins/util/handleProteinUpdateError';
import * as sequtil from 'molbio/SeqUtil';

const ProteinAnnotationEditorActions = {
  newAnnotation(proteinId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_NEW_ANNOTATION,
      proteinId,
    });
  },

  setName(proteinId: string, name: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_NAME,
      proteinId,
      name,
    });
  },

  setType(proteinId: string, annotationType: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_TYPE,
      proteinId,
      annotationType,
    });
  },

  setNotes(proteinId: string, annotationNotes: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_NOTES,
      proteinId,
      annotationNotes,
    });
  },

  setDisplayStart(proteinId: string, displayStart: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_DISPLAY_START,
      proteinId,
      displayStart,
    });
  },

  setDisplayEnd(proteinId: string, displayEnd: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_DISPLAY_END,
      proteinId,
      displayEnd,
    });
  },

  setColor(proteinId: string, color: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_COLOR,
      proteinId,
      color,
    });
  },

  /**
   * Save an annotation.
   */
  save(proteinId: string): void {
    const expectedVersionId = ProteinStore.getLatestVersionId(proteinId);
    const {name, type, displayStart, displayEnd, color, notes} =
      ProteinStore.getAnnotationEditorData(proteinId)!;
    const protein = ProteinStore.getProteinById(proteinId)!;
    const startIndex = 1;
    const proteinLength = protein.version.aminoAcids.length;
    const start = sequtil.displayStartToModel(Number(displayStart), startIndex);
    const end = sequtil.displayEndToModel(Number(displayEnd), proteinLength, startIndex);

    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_INITIAL_SAVE,
      proteinId,
    });

    const existingAnnotationId = ProteinStore.getSelectedAnnotationId(proteinId);
    let deferred;
    if (existingAnnotationId == null) {
      analytics.track('protein', 'created_annotation', {
        product: ANALYTICS_MODULE.MOLBIO,
        actionCategory: ACTION_CATEGORY.WRITE,
        protein_id: proteinId,
      });
      deferred = ProteinDAO.createAnnotation(
        proteinId,
        name,
        type,
        notes,
        start,
        end,
        color,
        expectedVersionId
      );
    } else {
      deferred = ProteinDAO.updateAnnotation(proteinId, existingAnnotationId, {
        name,
        type,
        notes,
        start,
        end,
        color,
        expectedVersionId,
      });
    }
    deferred
      .done((proteinResponse) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_UPDATE_COMPLETED,
          proteinId,
          newProtein: proteinResponse,
        });
        ProteinActions.fetchVersions(proteinId);
      })
      .fail(handleProteinUpdateError);
  },

  cancel(proteinId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_CANCEL,
      proteinId,
    });
  },

  delete(proteinId: string, annotationId: string): void {
    const expectedVersionId = ProteinStore.getLatestVersionId(proteinId);
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_INITIAL_DELETE,
      proteinId,
      annotationId,
    });

    ProteinDAO.deleteAnnotation(proteinId, annotationId, expectedVersionId)
      .done((proteinResponse) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_UPDATE_COMPLETED,
          proteinId,
          newProtein: proteinResponse,
        });
        ProteinActions.fetchVersions(proteinId);
      })
      .fail(handleProteinUpdateError);
  },

  /**
   * Called to acknowledge that the keyboard focus has been set on the annotation editor.
   */
  pendingFocusCompleted(proteinId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ANNOTATION_EDITOR_PENDING_FOCUS_COMPLETED,
      proteinId,
    });
  },
};

export default ProteinAnnotationEditorActions;
